import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import moment from "moment";
import Api from "api/Api";
import { listAllParsedInvoices } from "api/SpringApi";
import Toaster from "components/common/Toaster";

const truncateUrl = (url, max = 40) =>
  url && url.length > max ? url.slice(0, max) + "…" : url || "—";

const TABS = [
  { key: "all", label: "All" },
  { key: "success", label: "✓ Delivered" },
  { key: "failed", label: "✗ Failed" },
  { key: "pending", label: "— Not Fired" }
];

const statusBadge = inv => {
  if (!inv.webhookFired)
    return <span style={s.badge.pending}>— Not Fired</span>;
  const code = inv.webhookStatus;
  if (code >= 200 && code < 300)
    return <span style={s.badge.success}>✓ {code}</span>;
  return <span style={s.badge.failed}>✗ {code}</span>;
};

const WebhookLogs = ({ storeId, storeName }) => {
  const [all, setAll] = useState([]);
  const [tab, setTab] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(false);
  const [storeFilter, setStoreFilter] = useState("");
  const [subs, setSubs] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const toaster = useRef();

  // Always load on mount — no storeId needed when viewing globally
  useEffect(() => {
    load();
    loadSubs();
  }, [storeId]);

  const load = () => {
    setLoading(true);
    const request = storeId
      ? Api.get(`admin/parsedInvoice/list?storesId=${storeId}`)
      : listAllParsedInvoices();
    request
      .then(r => {
        setAll(r.data.data || []);
        setLoading(false);
      })
      .catch(() => {
        toaster.current && toaster.current.error("Failed to load");
        setLoading(false);
      });
  };

  const loadSubs = () => {
    setSubsLoading(true);
    const url = storeId
      ? `admin/webhookConfig/subscriptions?storesId=${storeId}`
      : `admin/webhookConfig/subscriptions/all`;
    Api.get(url)
      .then(r => {
        setSubs(r.data.data || []);
        setSubsLoading(false);
      })
      .catch(() => setSubsLoading(false));
  };

  const rows = all.filter(inv => {
    const storeMatch =
      !storeFilter ||
      (inv.storeName || "").toLowerCase().includes(storeFilter.toLowerCase()) ||
      (inv.branchName || "").toLowerCase().includes(storeFilter.toLowerCase());
    if (!storeMatch) return false;
    if (tab === "success")
      return (
        inv.webhookFired && inv.webhookStatus >= 200 && inv.webhookStatus < 300
      );
    if (tab === "failed")
      return (
        inv.webhookFired &&
        (inv.webhookStatus < 200 || inv.webhookStatus >= 300)
      );
    if (tab === "pending") return !inv.webhookFired;
    return true;
  });

  const counts = {
    all: all.length,
    success: all.filter(
      i => i.webhookFired && i.webhookStatus >= 200 && i.webhookStatus < 300
    ).length,
    failed: all.filter(
      i => i.webhookFired && (i.webhookStatus < 200 || i.webhookStatus >= 300)
    ).length,
    pending: all.filter(i => !i.webhookFired).length
  };

  return (
    <div className="roe-card-style mtb-15">
      <div className="roe-card-header module-header">
        <div className="flex-1 fs-16 demi-bold-text">
          <span className="hash"># </span>
          Webhook Event Logs {storeName ? `— ${storeName}` : "— All Stores"}
        </div>
        {!storeId && (
          <input
            type="text"
            className="form-control form-control-sm mr-2"
            style={{ maxWidth: 180 }}
            placeholder="Filter by store…"
            value={storeFilter}
            onChange={e => setStoreFilter(e.target.value)}
          />
        )}
        <button className="btn btn-outline-secondary btn-sm" onClick={load}>
          ↻ Refresh
        </button>
      </div>

      {/* Tab bar */}
      <div
        style={{
          display: "flex",
          gap: 8,
          padding: "12px 16px 0",
          borderBottom: "1px solid #dee2e6"
        }}
      >
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{ ...s.tab, ...(tab === t.key ? s.tabActive : {}) }}
          >
            {t.label}
            <span style={s.count}>{counts[t.key]}</span>
          </button>
        ))}
      </div>

      {/* Stats bar */}
      <div style={s.statsBar}>
        <div style={s.stat}>
          <span style={{ color: "#28a745", fontWeight: 700 }}>
            {counts.success}
          </span>{" "}
          Delivered
        </div>
        <div style={s.stat}>
          <span style={{ color: "#dc3545", fontWeight: 700 }}>
            {counts.failed}
          </span>{" "}
          Failed
        </div>
        <div style={s.stat}>
          <span style={{ color: "#6c757d", fontWeight: 700 }}>
            {counts.pending}
          </span>{" "}
          Not Fired
        </div>
        <div style={s.stat}>
          <span style={{ fontWeight: 700 }}>{counts.all}</span> Total Events
        </div>
      </div>

      {/* Active Push Subscriptions panel */}
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #dee2e6" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 8
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 13 }}>
            🔗 Active Push Subscriptions
          </span>
          <button
            className="btn btn-outline-secondary btn-sm"
            style={{ padding: "1px 8px", fontSize: 11 }}
            onClick={loadSubs}
          >
            ↻
          </button>
        </div>
        {subsLoading ? (
          <span className="text-muted" style={{ fontSize: 12 }}>
            Loading…
          </span>
        ) : subs.length === 0 ? (
          <span className="text-muted" style={{ fontSize: 12 }}>
            No active push subscriptions found.
          </span>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {subs.map(s => (
              <div
                key={s.subscriptionId}
                style={{
                  background: "#e8f5e9",
                  borderRadius: 6,
                  padding: "4px 12px",
                  fontSize: 12,
                  border: "1px solid #a5d6a7"
                }}
              >
                <span style={{ fontWeight: 600, color: "#2e7d32" }}>
                  {s.subscriptionId?.split("/").pop() || s.subscriptionId}
                </span>
                {s.pushUrl && (
                  <span
                    style={{ color: "#555", marginLeft: 6 }}
                    title={s.pushUrl}
                  >
                    → {truncateUrl(s.pushUrl, 50)}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <p className="text-muted p-3">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="alert alert-info m-3" style={{ fontSize: 13 }}>
          No events in this category.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table
            className="table table-sm table-bordered"
            style={{ fontSize: 12, margin: 0 }}
          >
            <thead
              style={{ background: "#f1f3f4", position: "sticky", top: 0 }}
            >
              <tr>
                <th>Fired At</th>
                <th>Status</th>
                <th>Consent</th>
                {!storeId && <th>Store</th>}
                <th>Order #</th>
                <th>Customer</th>
                <th>Amount</th>
                <th>Endpoint URL</th>
                <th>Parser</th>
                <th>Confidence</th>
                <th>Response</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((inv, i) => (
                <React.Fragment key={inv.parsedInvoiceId || i}>
                  <tr
                    style={{
                      cursor: inv.webhookResponse ? "pointer" : "default"
                    }}
                    onClick={() => setExpanded(expanded === i ? null : i)}
                  >
                    <td style={{ whiteSpace: "nowrap" }}>
                      {inv.webhookFiredAt ? (
                        moment(inv.webhookFiredAt).format("DD/MM/YY HH:mm:ss")
                      ) : (
                        <span style={{ color: "#aaa" }}>—</span>
                      )}
                    </td>
                    <td>{statusBadge(inv)}</td>
                    <td>
                      {inv.consentAccepted ? (
                        <span
                          style={{
                            color: "#28a745",
                            fontSize: 11,
                            fontWeight: 600
                          }}
                        >
                          ✓ Consent
                        </span>
                      ) : (
                        <span
                          style={{
                            color: "#f39c12",
                            fontSize: 11,
                            fontWeight: 600
                          }}
                        >
                          ⏱ Fallback
                        </span>
                      )}
                    </td>
                    {!storeId && (
                      <td>
                        <div style={{ fontWeight: 500 }}>
                          {inv.storeName || "—"}
                        </div>
                        <div style={{ color: "#888", fontSize: 11 }}>
                          {inv.branchName || ""}
                        </div>
                      </td>
                    )}
                    <td>
                      <strong>{inv.orderNumber || "—"}</strong>
                    </td>
                    <td>
                      <div>{inv.billName || "—"}</div>
                      <div style={{ color: "#888", fontSize: 11 }}>
                        {inv.customerEmail || ""}
                      </div>
                    </td>
                    <td>{inv.amount ? `€${inv.amount}` : "—"}</td>
                    <td
                      style={{
                        maxWidth: 160,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                      title={inv.webhookUrl}
                    >
                      {inv.webhookUrl ? (
                        <span style={{ color: "#1a73e8", fontSize: 11 }}>
                          {truncateUrl(inv.webhookUrl, 30)}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>{inv.parserSource || "—"}</td>
                    <td>
                      {inv.confidence != null
                        ? `${Math.round(inv.confidence * 100)}%`
                        : "—"}
                    </td>
                    <td>
                      {inv.webhookResponse ? (
                        <span style={{ color: "#1a73e8" }}>
                          {expanded === i ? "▲ Hide" : "▼ Show"}
                        </span>
                      ) : (
                        <span style={{ color: "#aaa" }}>—</span>
                      )}
                    </td>
                  </tr>
                  {expanded === i && inv.webhookResponse && (
                    <tr>
                      <td
                        colSpan={storeId ? 10 : 11}
                        style={{ background: "#f8f9fa", padding: 12 }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            marginBottom: 4,
                            color: "#555"
                          }}
                        >
                          <strong>Webhook URL:</strong> {inv.webhookUrl}
                          &nbsp;|&nbsp;<strong>HTTP:</strong>{" "}
                          {inv.webhookStatus}
                          &nbsp;|&nbsp;<strong>Fired:</strong>{" "}
                          {inv.webhookFiredAt
                            ? moment(inv.webhookFiredAt).format(
                                "DD/MM/YYYY HH:mm:ss"
                              )
                            : "—"}
                        </div>
                        <pre
                          style={{
                            fontSize: 11,
                            margin: 0,
                            maxHeight: 250,
                            overflow: "auto",
                            background: "#fff",
                            padding: 8,
                            border: "1px solid #dee2e6",
                            borderRadius: 4
                          }}
                        >
                          {(() => {
                            try {
                              return JSON.stringify(
                                JSON.parse(inv.webhookResponse),
                                null,
                                2
                              );
                            } catch {
                              return inv.webhookResponse;
                            }
                          })()}
                        </pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Toaster ref={toaster} />
    </div>
  );
};

const s = {
  tab: {
    background: "none",
    border: "none",
    borderBottom: "2px solid transparent",
    padding: "8px 14px",
    cursor: "pointer",
    fontSize: 13,
    color: "#555",
    display: "flex",
    alignItems: "center",
    gap: 6
  },
  tabActive: {
    borderBottom: "2px solid #1a73e8",
    color: "#1a73e8",
    fontWeight: 600
  },
  count: {
    background: "#e8eaf6",
    borderRadius: 10,
    padding: "1px 7px",
    fontSize: 11
  },
  statsBar: {
    display: "flex",
    gap: 24,
    padding: "10px 16px",
    background: "#fafafa",
    borderBottom: "1px solid #dee2e6",
    fontSize: 13
  },
  stat: { display: "flex", gap: 6, alignItems: "center" },
  badge: {
    success: { color: "#28a745", fontWeight: 600, fontSize: 12 },
    failed: { color: "#dc3545", fontWeight: 600, fontSize: 12 },
    pending: { color: "#6c757d", fontSize: 12 }
  }
};

const mapStateToProps = state => ({ ...state.themeChanger, ...state.storeID });
export default connect(mapStateToProps)(WebhookLogs);
